import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Checkbox, FormControlLabel, Grid, IconButton, MenuItem, TextField, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import CSVDropzone from '@/components/CSVDropzone';
import { ITEM_TYPES, ASSEMBLY_TYPES } from '@/components/ShipmentForm/constants/freightTypes';
import { buildSkuValues, formatServiceTime, isInventoryRangeInvalid } from './utils';
import DocumentLink from './DocumentLink';

// A SKU is not tied to a freight type, so offer every item type in one list.
const ITEM_TYPE_OPTIONS = Object.values(ITEM_TYPES)
    .flat()
    .filter((option, idx, all) => all.findIndex((o) => o.value === option.value) === idx);

const EMPTY_FORM = {
    sku: '',
    description: '',
    item_type_details: '',
    assembly_type: '',
    rate_class: '',
    service_time: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    inventory_min: '',
    inventory_max: '',
    serialized: true,
    sellable: true,
};

// Add / edit a single SKU (item_dimensions row). `sku` non-null => edit mode.
// Files are staged locally and only uploaded/removed by the caller's onSave.
const SKUModal = ({ open, sku, onClose, onSave, loading }) => {
    const isEdit = !!sku;
    const [form, setForm] = useState(EMPTY_FORM);
    const [newFiles, setNewFiles] = useState([]);
    const [removedDocIds, setRemovedDocIds] = useState([]);

    // Reseed each time the modal opens (add => blank, edit => the row).
    useEffect(() => {
        if (!open) return;
        setForm({
            sku: sku?.sku || '',
            description: sku?.description || '',
            item_type_details: sku?.item_type_details || '',
            assembly_type: sku?.assembly_type || '',
            rate_class: sku?.rate_class || '',
            service_time: sku?.service_time != null ? formatServiceTime(sku) : '',
            weight: sku?.weight ?? '',
            length: sku?.length ?? '',
            width: sku?.width ?? '',
            height: sku?.height ?? '',
            inventory_min: sku?.inventory_min ?? '',
            inventory_max: sku?.inventory_max ?? '',
            serialized: sku?.serialized ?? true,
            sellable: sku?.sellable ?? true,
        });
        setNewFiles([]);
        setRemovedDocIds([]);
    }, [open, sku]);

    const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
    const setFlag = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.checked }));

    const existingDocs = (sku?.documents || []).filter((d) => !removedDocIds.includes(d.item_dimension_document_id));

    const inventoryRangeInvalid = isInventoryRangeInvalid(form.inventory_min, form.inventory_max);
    // buildSkuValues owns normalization; this only gates the save button.
    const canSave = !!form.sku.trim() && !inventoryRangeInvalid && !loading;

    const handleSave = () => {
        const values = buildSkuValues(form);

        const removedDocs = (sku?.documents || [])
            .filter((d) => removedDocIds.includes(d.item_dimension_document_id))
            .map((d) => ({ item_dimension_document_id: d.item_dimension_document_id, link: d.link }));

        onSave({ values, newFiles, removedDocs });
    };

    return (
        <NavResponsiveModal open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <ModalHeader title={isEdit ? 'Edit SKU' : 'Add SKU'} onClose={onClose} />
            <ModalContent
                css={css`
                    max-height: 70vh;
                    overflow-y: auto;
                `}
            >
                <TextField
                    fullWidth
                    autoFocus
                    variant="outlined"
                    label="SKU"
                    value={form.sku}
                    onChange={setField('sku')}
                    css={css`
                        margin-bottom: 1rem;
                    `}
                />
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Description"
                    value={form.description}
                    onChange={setField('description')}
                    css={css`
                        margin-bottom: 1rem;
                    `}
                />
                <Grid
                    container
                    spacing={2}
                    css={css`
                        margin-bottom: 1rem;
                    `}
                >
                    <Grid item xs={6}>
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            label="Item type"
                            value={form.item_type_details}
                            onChange={setField('item_type_details')}
                        >
                            {ITEM_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            select
                            fullWidth
                            variant="outlined"
                            label="Assembly type"
                            value={form.assembly_type}
                            onChange={setField('assembly_type')}
                        >
                            {ASSEMBLY_TYPES.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.display}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={2}
                    css={css`
                        margin-bottom: 1rem;
                    `}
                >
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Rate class"
                            helperText="Prices this SKU on tariffs with rate class rates"
                            value={form.rate_class}
                            onChange={setField('rate_class')}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={2}
                    css={css`
                        margin-bottom: 0.25rem;
                    `}
                >
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="Service time"
                            placeholder="HH:MM"
                            value={form.service_time}
                            onChange={setField('service_time')}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            label="Weight"
                            value={form.weight}
                            onChange={setField('weight')}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={2}
                    css={css`
                        margin-bottom: 1rem;
                    `}
                >
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            label="Length"
                            value={form.length}
                            onChange={setField('length')}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            label="Width"
                            value={form.width}
                            onChange={setField('width')}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            label="Height"
                            value={form.height}
                            onChange={setField('height')}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={2}
                    css={css`
                        margin-bottom: 1rem;
                    `}
                >
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            label="Inventory min"
                            value={form.inventory_min}
                            onChange={setField('inventory_min')}
                            error={inventoryRangeInvalid}
                            inputProps={{ min: 0, step: 1 }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="number"
                            variant="outlined"
                            label="Inventory max"
                            value={form.inventory_max}
                            onChange={setField('inventory_max')}
                            error={inventoryRangeInvalid}
                            helperText={inventoryRangeInvalid ? 'Min must be less than max' : undefined}
                            inputProps={{ min: 0, step: 1 }}
                        />
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={2}
                    css={css`
                        margin-bottom: 1rem;
                    `}
                >
                    <Grid item xs={6}>
                        <FormControlLabel
                            label="Serialized"
                            control={
                                <Checkbox color="primary" checked={form.serialized} onChange={setFlag('serialized')} />
                            }
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <FormControlLabel
                            label="Sellable"
                            control={
                                <Checkbox color="primary" checked={form.sellable} onChange={setFlag('sellable')} />
                            }
                        />
                    </Grid>
                </Grid>

                <div
                    css={css`
                        font-weight: 700;
                        margin-bottom: 0.5rem;
                    `}
                >
                    Assembly Instructions
                </div>

                {existingDocs.map((doc) => (
                    <div
                        key={doc.item_dimension_document_id}
                        css={css`
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                        `}
                    >
                        <DocumentLink href={doc.link}>{doc.name}</DocumentLink>
                        <Tooltip title="Remove">
                            <IconButton
                                size="small"
                                aria-label={`Remove ${doc.name}`}
                                onClick={() => setRemovedDocIds((prev) => [...prev, doc.item_dimension_document_id])}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </div>
                ))}

                <CSVDropzone
                    accept={undefined}
                    multiple
                    label="Drag and Drop or Select"
                    fileTypeLabel="PDF, image, or video"
                    handleFileAdd={(file) => setNewFiles((prev) => [...prev, file])}
                />

                {newFiles.map((file, i) => (
                    <div
                        key={`${file.name}-${i}`}
                        css={css`
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            margin-top: 0.25rem;
                        `}
                    >
                        <span>{file.name}</span>
                        <Tooltip title="Remove">
                            <IconButton
                                size="small"
                                aria-label={`Remove ${file.name}`}
                                onClick={() => setNewFiles((prev) => prev.filter((_, idx) => idx !== i))}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </div>
                ))}
            </ModalContent>
            <ModalActions>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <PrimaryButton disabled={!canSave} loading={loading} onClick={handleSave}>
                    Save
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default SKUModal;
